<template>
  <div class="page-wrap">
    <div class="paper-layout">
      <!-- 文件夹侧栏 -->
      <el-card shadow="never" class="folder-panel">
        <div class="folder-header">
          <span class="folder-title">文件夹</span>
          <el-button type="text" icon="el-icon-plus" @click="openFolderDialog(null)">新建</el-button>
        </div>
        <div
          class="folder-item"
          :class="{ active: selectedFolderKey === 'all' }"
          @click="selectFolder('all')"
        >
          <i class="el-icon-files"></i>
          <span>全部试卷</span>
          <span class="folder-count">{{ folderSummary.totalCount || 0 }}</span>
        </div>
        <div
          class="folder-item"
          :class="{ active: selectedFolderKey === 'none' }"
          @click="selectFolder('none')"
        >
          <i class="el-icon-document"></i>
          <span>未分类</span>
          <span class="folder-count">{{ folderSummary.uncategorizedCount || 0 }}</span>
        </div>
        <el-tree
          v-if="folderTree.length"
          ref="folderTree"
          class="folder-tree"
          :data="folderTree"
          node-key="id"
          :props="{ label: 'name', children: 'children' }"
          highlight-current
          default-expand-all
          @node-click="onFolderNodeClick"
        >
          <span slot-scope="{ node, data }" class="tree-node">
            <i class="el-icon-folder"></i>
            <span class="tree-label">{{ node.label }}</span>
            <span class="folder-count">{{ data.paperCount || 0 }}</span>
          </span>
        </el-tree>
        <div v-else class="folder-empty">暂无文件夹，点击「新建」创建</div>
        <div v-if="selectedFolderId > 0" class="folder-actions">
          <el-button type="text" size="mini" @click="openFolderDialog(selectedFolderId)">子文件夹</el-button>
          <el-button type="text" size="mini" @click="renameFolder">重命名</el-button>
          <el-button type="text" size="mini" class="danger-text" @click="deleteFolder">删除</el-button>
        </div>
      </el-card>

      <!-- 试卷主区域 -->
      <div class="paper-main">
        <el-card shadow="never" class="filter-card">
          <div class="top-bar">
            <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap">
              <el-select v-model="filterForm.schoolYearId" placeholder="学年" clearable style="width:130px" @change="loadList">
                <el-option v-for="y in schoolYears" :key="y.id" :label="y.name" :value="y.id" />
              </el-select>
              <el-select v-model="filterForm.gradeId" placeholder="年级" clearable style="width:110px" @change="onFilterGradeChange">
                <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="g.id" />
              </el-select>
              <el-select v-model="filterForm.semesterId" placeholder="学期" clearable style="width:110px" @change="loadList">
                <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
              </el-select>
              <el-select v-model="filterForm.subjectId" placeholder="学科" clearable style="width:110px" @change="loadList">
                <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
              </el-select>
              <el-select v-model="filterForm.examTypeId" placeholder="考试类型" clearable style="width:120px" @change="loadList">
                <el-option v-for="e in examTypes" :key="e.id" :label="e.name" :value="e.id" />
              </el-select>
              <el-select v-model="filterForm.status" placeholder="状态" clearable style="width:110px" @change="loadList">
                <el-option label="草稿" :value="0" />
                <el-option label="待审核" :value="2" />
                <el-option label="已驳回" :value="3" />
                <el-option label="已发布" :value="1" />
              </el-select>
            </div>
            <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">新建试卷</el-button>
          </div>
          <div v-if="currentFolderLabel" class="folder-breadcrumb">
            当前位置：<b>{{ currentFolderLabel }}</b>
          </div>
        </el-card>

        <div v-loading="loading" class="paper-list">
          <el-empty v-if="!loading && tableData.length === 0" description="暂无试卷，点击新建开始组卷" />

          <div v-for="row in tableData" :key="row.id" class="paper-card">
            <div class="paper-header">
              <span class="paper-title">{{ row.title }}</span>
              <el-tag :type="paperStatusType(row.status)" size="small" style="margin-left:8px">
                {{ paperStatusLabel(row.status) }}
              </el-tag>
              <el-tag v-if="row.createdBy" size="mini" type="warning" style="margin-left:4px">教师提交</el-tag>
              <div class="paper-meta">
                <span v-if="getSchoolYearName(row.schoolYearId)" class="meta-chip">{{ getSchoolYearName(row.schoolYearId) }}</span>
                <span v-if="getGradeName(row.gradeId)" class="meta-chip">{{ getGradeName(row.gradeId) }}</span>
                <span v-if="getSemesterName(row.semesterId)" class="meta-chip">{{ getSemesterName(row.semesterId) }}</span>
                <span v-if="getSubjectName(row.subjectId)" class="meta-chip">{{ getSubjectName(row.subjectId) }}</span>
                <span v-if="getExamTypeName(row.examTypeId)" class="meta-chip">{{ getExamTypeName(row.examTypeId) }}</span>
                <span class="meta-chip q-count">{{ row.questionCount }} 题</span>
              </div>
            </div>
            <div v-if="row.description" class="paper-desc">{{ row.description }}</div>
            <div v-if="row.status === 3 && row.auditRemark" class="paper-audit-remark">驳回：{{ row.auditRemark }}</div>
            <div class="paper-footer">
              <span class="paper-time">{{ formatTime(row.createdAt) }}</span>
              <div class="paper-actions">
                <el-dropdown trigger="click" @command="cmd => movePaper(row, cmd)">
                  <el-button type="text" icon="el-icon-folder">移动到</el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item :command="0">未分类</el-dropdown-item>
                    <el-dropdown-item v-for="f in folderFlat" :key="f.id" :command="f.id">{{ f.displayName || f.name }}</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                <el-divider direction="vertical" />
                <el-button type="text" icon="el-icon-edit" @click="openEditDialog(row)" :disabled="row.status === 2">编辑</el-button>
                <el-divider direction="vertical" />
                <el-button type="text" icon="el-icon-document" @click="goDetail(row.id)">组卷</el-button>
                <template v-if="row.status === 2">
                  <el-divider direction="vertical" />
                  <el-button type="text" class="success-text" icon="el-icon-check" @click="approvePaper(row)">通过审核</el-button>
                  <el-divider direction="vertical" />
                  <el-button type="text" class="danger-text" icon="el-icon-close" @click="openRejectDialog(row)">驳回</el-button>
                </template>
                <template v-else>
                  <el-divider direction="vertical" />
                  <el-button
                    type="text"
                    :icon="row.status === 1 ? 'el-icon-download' : 'el-icon-upload'"
                    @click="toggleStatus(row)"
                  >{{ row.status === 1 ? '下架' : '发布' }}</el-button>
                </template>
                <el-divider direction="vertical" />
                <el-button type="text" icon="el-icon-share" @click="openShare(row)">分享</el-button>
                <el-divider direction="vertical" />
                <el-button type="text" icon="el-icon-delete" class="danger-text" @click="deletePaper(row)">删除</el-button>
              </div>
            </div>
          </div>
        </div>

        <el-pagination
          class="pagination"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
          :current-page="page + 1"
          :page-sizes="[10, 20, 50]"
          :page-size="size"
          :total="total"
          layout="total, sizes, prev, pager, next"
        />
      </div>
    </div>

    <!-- 新建/编辑试卷 -->
    <el-dialog :title="editMode ? '编辑试卷' : '新建试卷'" :visible.sync="dialogVisible" width="600px" @closed="resetForm">
      <el-form :model="editForm" :rules="formRules" ref="editForm" label-width="80px">
        <el-form-item label="试卷名称" prop="title">
          <el-input v-model="editForm.title" placeholder="请选择下方条件自动生成试卷名称" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="学年" :rules="[{required:true,message:'请选择学年'}]" prop="schoolYearId">
              <el-select v-model="editForm.schoolYearId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="y in schoolYears" :key="y.id" :label="y.name" :value="y.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="年级" :rules="[{required:true,message:'请选择年级'}]" prop="gradeId">
              <el-select v-model="editForm.gradeId" placeholder="必选" style="width:100%" @change="onEditGradeChange">
                <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="g.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="学期" :rules="[{required:true,message:'请选择学期'}]" prop="semesterId">
              <el-select v-model="editForm.semesterId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="学科" :rules="[{required:true,message:'请选择学科'}]" prop="subjectId">
              <el-select v-model="editForm.subjectId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="s in subjects" :key="s.id" :label="s.name" :value="s.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="考试类型" :rules="[{required:true,message:'请选择考试类型'}]" prop="examTypeId">
              <el-select v-model="editForm.examTypeId" placeholder="必选" style="width:100%" @change="generatePaperTitle">
                <el-option v-for="e in examTypes" :key="e.id" :label="e.name" :value="e.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="试卷说明">
          <el-input type="textarea" v-model="editForm.description" :rows="3" placeholder="可选，填写试卷说明" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="savePaper">保存</el-button>
      </template>
    </el-dialog>

    <!-- 新建/重命名文件夹 -->
    <el-dialog :title="folderDialogTitle" :visible.sync="folderDialogVisible" width="400px">
      <el-input v-model="folderForm.name" placeholder="请输入文件夹名称" maxlength="50" show-word-limit />
      <template #footer>
        <el-button @click="folderDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="folderSaving" @click="saveFolder">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog title="分享试卷" :visible.sync="shareVisible" width="400px">
      <div v-if="sharePaper.id" class="share-content">
        <div class="share-paper-info">
          <h4>{{ sharePaper.title }}</h4>
          <p>{{ sharePaper.questionCount }} 道题</p>
        </div>
        <div class="share-section">
          <label>分享链接</label>
          <div class="share-link-box">
            <el-input v-model="shareLink" readonly size="small" />
            <el-button type="primary" size="small" @click="copyLink">复制</el-button>
          </div>
        </div>
        <div class="share-section">
          <label>二维码</label>
          <div class="qr-code-box">
            <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="二维码" />
            <div v-else class="qr-placeholder">加载中...</div>
          </div>
          <el-button type="text" @click="downloadQR" :disabled="!qrCodeUrl">下载二维码</el-button>
        </div>
      </div>
    </el-dialog>

    <el-dialog title="驳回试卷" :visible.sync="rejectVisible" width="480px">
      <el-input type="textarea" v-model="rejectRemark" :rows="4" placeholder="请填写驳回原因（可选）" />
      <template #footer>
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="rejecting" @click="confirmReject">确认驳回</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import request from '../../utils/request'
import filterLoader from '../../mixins/filterLoader'

export default {
  name: 'PaperList',
  mixins: [filterLoader],
  data() {
    return {
      filterForm: { schoolYearId: null, gradeId: null, semesterId: null, subjectId: null, examTypeId: null, status: null },
      schoolYears: [],
      grades: [],
      semesters: [],
      subjects: [],
      examTypes: [],
      tableData: [],
      page: 0,
      size: 10,
      total: 0,
      loading: false,
      dialogVisible: false,
      editMode: false,
      saving: false,
      editForm: { id: null, title: '', description: '', schoolYearId: null, gradeId: null, semesterId: null, subjectId: null, examTypeId: null },
      formRules: {
        title: [{ required: true, message: '请输入试卷名称', trigger: 'blur' }]
      },
      shareVisible: false,
      sharePaper: {},
      shareLink: '',
      qrCodeUrl: '',
      rejectVisible: false,
      rejectRemark: '',
      rejectTarget: null,
      rejecting: false,
      folderTree: [],
      folderFlat: [],
      folderSummary: { totalCount: 0, uncategorizedCount: 0 },
      selectedFolderKey: 'all',
      selectedFolderId: null,
      folderDialogVisible: false,
      folderDialogMode: 'create',
      folderForm: { name: '', parentId: 0 },
      folderSaving: false
    }
  },
  computed: {
    currentFolderLabel() {
      if (this.selectedFolderKey === 'all') return '全部试卷'
      if (this.selectedFolderKey === 'none') return '未分类'
      const f = this.folderFlat.find(x => x.id === this.selectedFolderId)
      return f ? f.name.trim() : ''
    },
    folderDialogTitle() {
      return this.folderDialogMode === 'rename' ? '重命名文件夹' : '新建文件夹'
    },
    createFolderId() {
      if (this.selectedFolderId > 0) return this.selectedFolderId
      return null
    }
  },
  created() {
    this.loadGradeAndSubject()
    this.loadStageDictBundle(null)
    this.loadDictLookup()
    this.loadFolders()
    this.loadList()
  },
  methods: {
    loadFolders() {
      request.get('/paper/folder/tree').then(data => {
        this.folderTree = data.folders || []
        this.folderSummary = data.summary || { totalCount: 0, uncategorizedCount: 0 }
      })
      request.get('/paper/folder/flat').then(data => {
        this.folderFlat = data || []
      })
    },
    listFolderIdParam() {
      if (this.selectedFolderKey === 'all') return undefined
      if (this.selectedFolderKey === 'none') return 0
      return this.selectedFolderId
    },
    selectFolder(key, folderId) {
      this.selectedFolderKey = key
      this.selectedFolderId = folderId || null
      this.page = 0
      if (this.$refs.folderTree) {
        this.$refs.folderTree.setCurrentKey(null)
      }
      this.loadList()
    },
    onFolderNodeClick(data) {
      this.selectedFolderKey = String(data.id)
      this.selectedFolderId = data.id
      this.page = 0
      this.loadList()
    },
    openFolderDialog(parentId) {
      this.folderDialogMode = 'create'
      this.folderForm = { name: '', parentId: parentId || 0 }
      this.folderDialogVisible = true
    },
    renameFolder() {
      if (!this.selectedFolderId) return
      const f = this.folderFlat.find(x => x.id === this.selectedFolderId)
      this.folderDialogMode = 'rename'
      this.folderForm = { name: f ? (f.name || '').trim() : '', id: this.selectedFolderId }
      this.folderDialogVisible = true
    },
    saveFolder() {
      if (!this.folderForm.name || !this.folderForm.name.trim()) {
        this.$message.warning('请输入文件夹名称')
        return
      }
      this.folderSaving = true
      const req = this.folderDialogMode === 'rename'
        ? request.put(`/paper/folder/${this.folderForm.id}`, { name: this.folderForm.name.trim() })
        : request.post('/paper/folder', { name: this.folderForm.name.trim(), parentId: this.folderForm.parentId || 0 })
      req.then(() => {
        this.$message.success(this.folderDialogMode === 'rename' ? '已重命名' : '文件夹已创建')
        this.folderDialogVisible = false
        this.loadFolders()
      }).finally(() => { this.folderSaving = false })
    },
    deleteFolder() {
      if (!this.selectedFolderId) return
      this.$confirm('删除后，该文件夹内的试卷将移至「未分类」，确认删除？', '提示', { type: 'warning' }).then(() => {
        request.delete(`/paper/folder/${this.selectedFolderId}`).then(() => {
          this.$message.success('已删除')
          this.selectFolder('all')
          this.loadFolders()
        })
      }).catch(() => {})
    },
    movePaper(row, folderId) {
      request.put(`/paper/folder/move-paper/${row.id}`, { folderId: folderId || null }).then(() => {
        this.$message.success('已移动')
        this.loadList()
        this.loadFolders()
      })
    },
    syncStageDictsByGradeId(gradeId) {
      const grade = (this.grades || []).find(g => g.id === gradeId)
      const stageId = grade ? grade.stageId : null
      return this.loadStageDictBundle(stageId)
    },
    remapFilterDictByName(names = {}) {
      const schoolYearName = names.schoolYearName || this.getSchoolYearName(this.filterForm.schoolYearId)
      const semesterName = names.semesterName || this.getSemesterName(this.filterForm.semesterId)
      const examTypeName = names.examTypeName || this.getExamTypeName(this.filterForm.examTypeId)
      if (schoolYearName) {
        const y = this.schoolYears.find(x => x.name === schoolYearName)
        this.filterForm.schoolYearId = y ? y.id : null
      }
      if (semesterName) {
        const s = this.semesters.find(x => x.name === semesterName)
        this.filterForm.semesterId = s ? s.id : null
      }
      if (examTypeName) {
        const e = this.examTypes.find(x => x.name === examTypeName)
        this.filterForm.examTypeId = e ? e.id : null
      }
    },
    remapEditDictByName(names = {}) {
      const schoolYearName = names.schoolYearName || this.getSchoolYearName(this.editForm.schoolYearId)
      const semesterName = names.semesterName || this.getSemesterName(this.editForm.semesterId)
      const examTypeName = names.examTypeName || this.getExamTypeName(this.editForm.examTypeId)
      if (schoolYearName) {
        const y = this.schoolYears.find(x => x.name === schoolYearName)
        this.editForm.schoolYearId = y ? y.id : null
      }
      if (semesterName) {
        const s = this.semesters.find(x => x.name === semesterName)
        this.editForm.semesterId = s ? s.id : null
      }
      if (examTypeName) {
        const e = this.examTypes.find(x => x.name === examTypeName)
        this.editForm.examTypeId = e ? e.id : null
      }
    },
    onFilterGradeChange(gradeId) {
      const names = {
        schoolYearName: this.getSchoolYearName(this.filterForm.schoolYearId),
        semesterName: this.getSemesterName(this.filterForm.semesterId),
        examTypeName: this.getExamTypeName(this.filterForm.examTypeId)
      }
      this.syncStageDictsByGradeId(gradeId).then(() => {
        this.remapFilterDictByName(names)
        this.loadList()
      })
    },
    onEditGradeChange() {
      const names = {
        schoolYearName: this.getSchoolYearName(this.editForm.schoolYearId),
        semesterName: this.getSemesterName(this.editForm.semesterId),
        examTypeName: this.getExamTypeName(this.editForm.examTypeId)
      }
      this.syncStageDictsByGradeId(this.editForm.gradeId).then(() => {
        this.remapEditDictByName(names)
        this.generatePaperTitle()
      })
    },
    loadList() {
      this.loading = true
      const { schoolYearId, gradeId, semesterId, subjectId, examTypeId, status } = this.filterForm
      const folderId = this.listFolderIdParam()
      const params = { page: this.page, size: this.size, schoolYearId, gradeId, semesterId, subjectId, examTypeId, status }
      if (folderId !== undefined) params.folderId = folderId
      request.get('/paper/list', { params })
        .then(data => {
          this.tableData = data.content || []
          this.total = data.totalElements || 0
        }).finally(() => { this.loading = false })
    },
    handlePageChange(val) { this.page = val - 1; this.loadList() },
    handleSizeChange(val) { this.size = val; this.page = 0; this.loadList() },
    openCreateDialog() {
      this.editMode = false
      this.editForm = { id: null, title: '', description: '', schoolYearId: null, gradeId: null, semesterId: null, subjectId: null, examTypeId: null }
      this.dialogVisible = true
    },
    generatePaperTitle() {
      const { schoolYearId, gradeId, semesterId, subjectId, examTypeId } = this.editForm
      const parts = []
      if (schoolYearId) parts.push(this.getSchoolYearName(schoolYearId))
      if (gradeId) parts.push(this.getGradeName(gradeId))
      if (semesterId) parts.push(this.getSemesterName(semesterId))
      if (subjectId) parts.push(this.getSubjectName(subjectId))
      if (examTypeId) parts.push(this.getExamTypeName(examTypeId))
      if (parts.length > 0) {
        this.editForm.title = parts.join('') + '试卷'
      }
    },
    openEditDialog(row) {
      this.editMode = true
      this.editForm = {
        id: row.id,
        title: row.title,
        description: row.description || '',
        schoolYearId: row.schoolYearId,
        gradeId: row.gradeId,
        semesterId: row.semesterId,
        subjectId: row.subjectId,
        examTypeId: row.examTypeId
      }
      if (row.gradeId) {
        this.syncStageDictsByGradeId(row.gradeId)
      }
      this.dialogVisible = true
    },
    resetForm() {
      this.$refs.editForm && this.$refs.editForm.resetFields()
    },
    savePaper() {
      this.$refs.editForm.validate(valid => {
        if (!valid) return
        this.saving = true
        const payload = { ...this.editForm }
        if (!this.editMode && this.createFolderId) {
          payload.folderId = this.createFolderId
        }
        const req = this.editMode
          ? request.put(`/paper/${this.editForm.id}`, payload)
          : request.post('/paper', payload)
        req.then(() => {
          this.$message.success(this.editMode ? '修改成功' : '创建成功')
          this.dialogVisible = false
          this.loadList()
          this.loadFolders()
        }).catch(() => {}).finally(() => { this.saving = false })
      })
    },
    toggleStatus(row) {
      if (row.status === 2) {
        this.$message.warning('待审核试卷请使用「通过审核」或「驳回」')
        return
      }
      const newStatus = row.status === 1 ? 0 : 1
      const label = newStatus === 1 ? '发布' : '下架'
      this.$confirm(`确认${label}该试卷？`, '提示', { type: 'warning' }).then(() => {
        request.put(`/paper/${row.id}/status`, { status: newStatus }).then(() => {
          this.$message.success(`已${label}`)
          this.loadList()
        })
      }).catch(() => {})
    },
    paperStatusLabel(status) {
      const map = { 0: '草稿', 1: '已发布', 2: '待审核', 3: '已驳回' }
      return map[status] || '未知'
    },
    paperStatusType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'warning'
      if (status === 3) return 'danger'
      return 'info'
    },
    approvePaper(row) {
      this.$confirm('确认通过审核并发布该试卷？', '通过审核', { type: 'warning' }).then(() => {
        request.put(`/paper/${row.id}/approve`).then(() => {
          this.$message.success('已通过审核并发布')
          this.loadList()
        })
      }).catch(() => {})
    },
    openRejectDialog(row) {
      this.rejectTarget = row
      this.rejectRemark = ''
      this.rejectVisible = true
    },
    confirmReject() {
      if (!this.rejectTarget) return
      this.rejecting = true
      request.put(`/paper/${this.rejectTarget.id}/reject`, { remark: this.rejectRemark }).then(() => {
        this.$message.success('已驳回')
        this.rejectVisible = false
        this.loadList()
      }).finally(() => { this.rejecting = false })
    },
    deletePaper(row) {
      this.$confirm('确认删除该试卷？题目不会被删除', '警告', { type: 'warning' }).then(() => {
        request.delete(`/paper/${row.id}`).then(() => {
          this.$message.success('已删除')
          this.loadList()
          this.loadFolders()
        })
      }).catch(() => {})
    },
    goDetail(id) {
      this.$router.push(`/paper/${id}`)
    },
    getGradeName(id) {
      const g = this.grades.find(x => x.id === id)
      return g ? g.name : this.getDictDisplayName(id)
    },
    getSubjectName(id) {
      const s = this.subjects.find(x => x.id === id)
      return s ? s.name : this.getDictDisplayName(id)
    },
    getExamTypeName(id) {
      const e = this.examTypes.find(x => x.id === id)
      return e ? e.name : this.getDictDisplayName(id)
    },
    getSchoolYearName(id) {
      const y = this.schoolYears.find(x => x.id === id)
      return y ? y.name : this.getDictDisplayName(id)
    },
    getSemesterName(id) {
      const s = this.semesters.find(x => x.id === id)
      return s ? s.name : this.getDictDisplayName(id)
    },
    formatTime(t) {
      if (!t) return ''
      return t.replace('T', ' ').substring(0, 16)
    },
    openShare(row) {
      if (row.status !== 1) {
        this.$message.warning('请先发布试卷后再分享')
        return
      }
      this.sharePaper = row
      this.shareVisible = true
      this.loadShareInfo(row.id)
    },
    loadShareInfo(paperId) {
      request.get(`/paper/${paperId}/share`).then(data => {
        this.shareLink = data.link
        this.qrCodeUrl = data.qrCodeUrl
      })
    },
    copyLink() {
      navigator.clipboard.writeText(this.shareLink).then(() => {
        this.$message.success('链接已复制')
      })
    },
    downloadQR() {
      if (!this.qrCodeUrl) return
      const a = document.createElement('a')
      a.href = this.qrCodeUrl
      a.download = `试卷${this.sharePaper.id}二维码.png`
      a.click()
    }
  }
}
</script>

<style scoped>
.paper-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.folder-panel {
  width: 220px;
  flex-shrink: 0;
}
.folder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.folder-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}
.folder-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  margin-bottom: 4px;
}
.folder-item:hover { background: #f5f7fa; }
.folder-item.active {
  background: #e6f4ff;
  color: #1677ff;
}
.folder-count {
  margin-left: auto;
  font-size: 12px;
  color: #999;
}
.folder-tree { margin-top: 4px; }
.tree-node {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  font-size: 13px;
  padding-right: 4px;
}
.tree-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
.folder-empty {
  font-size: 12px;
  color: #bbb;
  padding: 8px 4px;
  line-height: 1.5;
}
.folder-actions {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.paper-main { flex: 1; min-width: 0; }
.folder-breadcrumb {
  margin-top: 12px;
  font-size: 13px;
  color: #666;
}
.top-bar { display: flex; align-items: center; justify-content: space-between; }
.filter-card { margin-bottom: 16px; }
.paper-list { display: flex; flex-direction: column; gap: 10px; }
.paper-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 16px;
  transition: box-shadow 0.2s;
}
.paper-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.08); border-color: #d0e6ff; }
.paper-header { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-bottom: 8px; }
.paper-title { font-size: 15px; font-weight: 600; color: #222; }
.paper-meta { display: flex; gap: 4px; margin-left: 8px; }
.meta-chip { padding: 1px 8px; background: #f5f5f5; border-radius: 3px; font-size: 12px; color: #777; }
.meta-chip.q-count { background: #e6f4ff; color: #1677ff; }
.paper-desc { font-size: 13px; color: #888; margin-bottom: 8px; line-height: 1.5; }
.paper-footer { display: flex; align-items: center; justify-content: space-between; border-top: 1px solid #f5f5f5; padding-top: 8px; margin-top: 4px; }
.paper-time { font-size: 12px; color: #bbb; }
.paper-actions { display: flex; align-items: center; flex-wrap: wrap; }
.paper-actions .el-divider--vertical { margin: 0 2px; }
.danger-text { color: #ff4d4f; }
.success-text { color: #52c41a; }
.paper-audit-remark { font-size: 12px; color: #cf1322; margin-bottom: 8px; }
.pagination { margin-top: 16px; text-align: right; }
</style>
